import { useState, type ReactNode } from 'react';
import { statementsApi } from '../../api/services';
import type { CashFlowAccountDto, CashFlowActivity, CashFlowLineDto, EquityRowDto } from '../../api/types';
import {
  Alert, Button, Card, Field, Input, Loadable, Modal, Select, Stat, Table, firstOfMonthIso, fmtDate, fmtMoney, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PERM, useCan } from '../../ui/access';

export const ACTIVITY_LABEL: Record<CashFlowActivity, string> = {
  EFECTIVO: 'Efectivo (cajas y bancos)', OPERACION: 'Operación', INVERSION: 'Inversión', FINANCIAMIENTO: 'Financiamiento',
};

function RangeBar({ onApply, children }: { onApply: (from: string, to: string) => void; children?: ReactNode }) {
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  return (
    <div className="acc-toolbar">
      <Field label="Desde"><Input type="date" value={from} onChange={setFrom} /></Field>
      <Field label="Hasta"><Input type="date" value={to} onChange={setTo} /></Field>
      <Button kind="primary" onClick={() => onApply(from, to)}>Consultar</Button>
      {children}
    </div>
  );
}

/** Estado de flujo de efectivo (método directo por contrapartida). */
export function CashFlowReport() {
  const [range, setRange] = useState({ from: firstOfMonthIso(), to: todayIso() });
  const flow = useAsync(() => statementsApi.cashFlow(range.from, range.to), [range.from, range.to]);
  const [config, setConfig] = useState(false);
  const { run, busy } = useAction();
  return (
    <>
      <RangeBar onApply={(from, to) => setRange({ from, to })}>
        <Button onClick={() => setConfig(true)}>Configurar cuentas…</Button>
        <Button busy={busy} onClick={() => run(() => statementsApi.exportCsv('cash-flow', range.from, range.to))}>Exportar CSV</Button>
      </RangeBar>
      <Loadable state={flow}>
        {(f) => (
          <>
            {f.cashAccounts.length === 0
              ? <Alert kind="warn">Marque primero sus cuentas de efectivo (cajas y bancos) con "Configurar cuentas…".</Alert>
              : (
                <>
                  <div className="acc-stats">
                    <Stat label="Efectivo al inicio" value={fmtMoney(f.openingCash)} />
                    {(['OPERACION', 'INVERSION', 'FINANCIAMIENTO'] as CashFlowActivity[]).map((a) => (
                      <Stat key={a} label={`Actividades de ${ACTIVITY_LABEL[a].toLowerCase()}`} value={fmtMoney(f.byActivity[a] ?? 0)} tone={(f.byActivity[a] ?? 0) < 0 ? 'neg' : 'pos'} />
                    ))}
                    <Stat label="Efectivo al final" value={fmtMoney(f.closingCash)} />
                  </div>
                  <Table<CashFlowLineDto> rows={f.lines} rowKey={(l) => `${l.activity}|${l.accountNumber}`} empty="Sin movimientos de efectivo en el período." columns={[
                    { key: 'a', title: 'Actividad', render: (l) => ACTIVITY_LABEL[l.activity] },
                    { key: 'c', title: 'Cuenta contraria', render: (l) => `${l.accountNumber} · ${l.accountName}` },
                    { key: 'm', title: 'Entradas (+) / salidas (−)', align: 'right', render: (l) => fmtMoney(l.amount) },
                  ]} />
                  <p className="acc-muted">Efectivo: {f.cashAccounts.join('; ')}. Montos en {f.currency}, del {fmtDate(f.dateFrom)} al {fmtDate(f.dateTo)}.</p>
                </>
              )}
            {f.notes.map((n) => <Alert key={n} kind={n.includes('diferencia') ? 'warn' : 'info'}>{n}</Alert>)}
          </>
        )}
      </Loadable>
      {config && <CashFlowConfig onClose={() => setConfig(false)} onSaved={() => { setConfig(false); flow.reload(); }} />}
    </>
  );
}

function CashFlowConfig({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const accounts = useAsync(() => statementsApi.cashFlowAccounts(), []);
  const canEdit = useCan(PERM.setup);
  return (
    <Modal title="Cuentas del flujo de efectivo" onClose={onClose} size="lg">
      <Loadable state={accounts}>{(a) => <CashFlowConfigForm initial={a} canEdit={canEdit} onClose={onClose} onSaved={onSaved} />}</Loadable>
    </Modal>
  );
}

function CashFlowConfigForm({ initial, canEdit, onClose, onSaved }: { initial: CashFlowAccountDto[]; canEdit: boolean; onClose: () => void; onSaved: () => void }) {
  const [rows, setRows] = useState<CashFlowAccountDto[]>(initial);
  const [add, setAdd] = useState({ account: '', activity: 'EFECTIVO' as CashFlowActivity });
  const { run, busy } = useAction();
  return (
    <>
      <Alert kind="info">
        Marque como Efectivo sus cuentas de caja y bancos. Asigne Inversión a activos fijos e inversiones, y Financiamiento a préstamos y aportes de socios.
        Si marca una cuenta madre, aplica a todas sus cuentas hijas. Lo que no se asigne cuenta como Operación (el patrimonio, como Financiamiento).
      </Alert>
      <Table<CashFlowAccountDto & { i: number }> rows={rows.map((r, i) => ({ ...r, i }))} rowKey={(r) => r.accountNumber} empty="Todavía no hay cuentas asignadas." columns={[
        { key: 'c', title: 'Cuenta', render: (r) => `${r.accountNumber} · ${r.accountName}` },
        { key: 'a', title: 'Actividad', render: (r) => (canEdit
          ? <Select value={r.activity} onChange={(v) => setRows(rows.map((x, k) => (k === r.i ? { ...x, activity: v } : x)))} options={(Object.keys(ACTIVITY_LABEL) as CashFlowActivity[]).map((k) => ({ value: k, label: ACTIVITY_LABEL[k] }))} />
          : ACTIVITY_LABEL[r.activity]) },
        { key: 'x', title: '', render: (r) => (canEdit ? <Button kind="link" onClick={() => setRows(rows.filter((_, k) => k !== r.i))}>Quitar</Button> : null) },
      ]} />
      {canEdit && (
        <Card title="Agregar cuenta">
          <div className="acc-grid-form">
            <Field label="Cuenta (de detalle o madre)"><AccountPicker value={add.account} onChange={(v) => setAdd({ ...add, account: v })} /></Field>
            <Field label="Actividad"><Select value={add.activity} onChange={(v) => setAdd({ ...add, activity: v })} options={(Object.keys(ACTIVITY_LABEL) as CashFlowActivity[]).map((k) => ({ value: k, label: ACTIVITY_LABEL[k] }))} /></Field>
          </div>
          <Button disabled={!add.account || rows.some((r) => r.accountNumber === add.account)} onClick={() => { setRows([...rows, { accountNumber: add.account, accountName: '', activity: add.activity }]); setAdd({ ...add, account: '' }); }}>Agregar</Button>
        </Card>
      )}
      <div className="acc-actions">
        <Button onClick={onClose}>Cerrar</Button>
        {canEdit && <Button kind="primary" busy={busy} onClick={() => run(async () => { await statementsApi.saveCashFlowAccounts(rows); onSaved(); }, { success: 'Configuración guardada.' })}>Guardar</Button>}
      </div>
    </>
  );
}

/** Estado de cambios en el patrimonio. */
export function EquityReport() {
  const [range, setRange] = useState({ from: `${todayIso().slice(0, 4)}-01-01`, to: todayIso() });
  const report = useAsync(() => statementsApi.equityChanges(range.from, range.to), [range.from, range.to]);
  const { run, busy } = useAction();
  return (
    <>
      <RangeBar onApply={(from, to) => setRange({ from, to })}>
        <Button busy={busy} onClick={() => run(() => statementsApi.exportCsv('equity-changes', range.from, range.to))}>Exportar CSV</Button>
      </RangeBar>
      <Loadable state={report}>
        {(r) => (
          <>
            <Table<EquityRowDto> rows={r.rows} rowKey={(x) => x.accountNumber || 'calc'} empty="No hay cuentas de patrimonio con saldo." columns={[
              { key: 'c', title: 'Cuenta', render: (x) => (x.accountNumber ? `${x.accountNumber} · ${x.accountName}` : <em>{x.accountName}</em>) },
              money<EquityRowDto>('o', 'Saldo inicial', (x) => x.opening),
              money<EquityRowDto>('i', 'Aumentos', (x) => x.increases),
              money<EquityRowDto>('d', 'Disminuciones', (x) => x.decreases),
              money<EquityRowDto>('f', 'Saldo final', (x) => x.closing),
            ]} footer={
              <tr><td><strong>Total patrimonio</strong></td><td style={{ textAlign: 'right' }}><strong>{fmtMoney(r.totalOpening)}</strong></td><td /><td />
                <td style={{ textAlign: 'right' }}><strong>{fmtMoney(r.totalClosing)}</strong></td></tr>
            } />
            <p className="acc-muted">Montos en {r.currency}, del {fmtDate(r.dateFrom)} al {fmtDate(r.dateTo)}.</p>
            {r.notes.map((n) => <Alert key={n}>{n}</Alert>)}
          </>
        )}
      </Loadable>
    </>
  );
}
